import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useGame, POWER_PLANT_SPECS, calcPowerPlantIncome } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import PlantStatus from "./components/PlantStatus";
import ContractPanel from "./components/ContractPanel";
import { getLandValueByLatLng } from "@/mocks/landValues";

export default function PowerPlantPage() {
  const { plantId } = useParams<{ plantId: string }>();
  const { powerPlants, updatePowerPlant, performMaintenance, blocks } = useGame();
  const { user, spendCoins } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);

  const plant = plantId ? powerPlants[plantId] : null;
  const isOwner = !!(user && plant && plant.ownerId === user.id);

  // 地価係数
  const landBlock = plant ? Object.values(blocks).find((b) => b.blockId === plant.blockId) : null;
  const blockLat = landBlock?.lat ?? 35.6812;
  const blockLng = landBlock?.lng ?? 139.7671;
  const lv = getLandValueByLatLng(blockLat, blockLng);

  // 契約中の合計MW
  const contractedMW = plant
    ? plant.powerContracts.filter((c) => c.status === "active").reduce((s, c) => s + c.contractedMW, 0)
    : 0;
  const hourlyIncome = plant
    ? calcPowerPlantIncome(contractedMW, blockLat, blockLng, plant.operatingStatus)
    : 0;

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";

  const handleMaintenance = () => {
    if (!plant) return;
    const ok = performMaintenance(plant.id, spendCoins);
    if (ok) {
      setMsg({ text: `メンテナンス完了！効率が回復しました (-${plant.maintenanceCost.toLocaleString()} coins)`, type: "success" });
    } else {
      setMsg({ text: "コインが不足しています", type: "error" });
    }
    setTimeout(() => setMsg(null), 3000);
  };

  const handleToggle = () => {
    if (!plant) return;
    const newStatus = plant.operatingStatus === "running" ? "offline" : "running";
    updatePowerPlant(plant.id, { operatingStatus: newStatus });
    setMsg({ text: newStatus === "running" ? "発電所を起動しました" : "発電所を停止しました", type: "success" });
    setTimeout(() => setMsg(null), 3000);
  };

  if (!plant) {
    return (
      <div className={`min-h-screen ${pageBg}`}>
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-400/10 rounded-2xl mb-4">
            <i className="ri-flashlight-line text-3xl text-red-400"></i>
          </div>
          <h2 className={`text-xl font-bold mb-2 ${textPrimary}`}>発電所が見つかりません</h2>
          <p className={`text-sm mb-6 ${textSecondary}`}>この発電所は存在しないか、削除された可能性があります</p>
          <button onClick={() => navigate("/mypage")} className="bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold rounded-full px-6 py-2.5 cursor-pointer whitespace-nowrap">
            マイページへ戻る
          </button>
        </div>
        <AuthModal />
      </div>
    );
  }

  const spec = POWER_PLANT_SPECS[plant.type];

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => navigate("/mypage")} className={`text-sm cursor-pointer hover:text-amber-400 transition-colors ${textSecondary}`}>マイページ</button>
          <i className="ri-arrow-right-s-line text-gray-500 text-sm"></i>
          <span className={`text-sm ${textPrimary}`}>{plant.name}</span>
        </div>

        {msg && (
          <div className={`mb-4 rounded-xl px-4 py-3 text-sm flex items-center gap-2 ${msg.type === "success" ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-red-400/10 text-red-400 border border-red-400/20"}`}>
            <i className={msg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
            {msg.text}
          </div>
        )}

        {/* 収益サマリー */}
        <div className={`rounded-2xl border p-5 mb-6 ${isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200"}`}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{lv.flag}</span>
              <div>
                <p className={`text-xs ${textSecondary}`}>地域</p>
                <p className={`text-sm font-bold ${textPrimary}`}>{lv.country}</p>
              </div>
            </div>
            <div className={`h-8 w-px ${isDark ? "bg-white/10" : "bg-gray-200"}`}></div>
            <div>
              <p className={`text-xs ${textSecondary}`}>地価係数</p>
              <p className="text-sm font-bold text-amber-400">×{lv.landMultiplier}</p>
            </div>
            <div className={`h-8 w-px ${isDark ? "bg-white/10" : "bg-gray-200"}`}></div>
            <div>
              <p className={`text-xs ${textSecondary}`}>契約中MW</p>
              <p className={`text-sm font-bold ${contractedMW > 0 ? "text-cyan-400" : "text-gray-500"}`}>{contractedMW} MW</p>
            </div>
            <div className={`h-8 w-px ${isDark ? "bg-white/10" : "bg-gray-200"}`}></div>
            <div>
              <p className={`text-xs ${textSecondary}`}>現在の収益</p>
              <p className={`text-lg font-black ${hourlyIncome > 0 ? "text-emerald-400" : "text-gray-500"}`}>
                {hourlyIncome > 0 ? `+${hourlyIncome}` : "0"} <span className="text-xs font-normal">coins/h</span>
              </p>
            </div>
            {hourlyIncome === 0 && (
              <div className={`ml-auto text-xs px-3 py-1.5 rounded-lg ${isDark ? "bg-red-400/10 text-red-400 border border-red-400/20" : "bg-red-50 text-red-500 border border-red-200"}`}>
                <i className="ri-error-warning-line mr-1"></i>
                {plant.operatingStatus !== "running" ? "停止中のため収益ゼロ" : "電力契約がないため収益ゼロ"}
              </div>
            )}
          </div>
        </div>

        <PlantStatus plant={plant} isOwner={isOwner} onMaintenance={handleMaintenance} onToggle={handleToggle} />

        <div className={`rounded-2xl border p-6 mb-6 ${isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200"}`}>
          <h2 className={`text-base font-bold mb-4 ${textPrimary}`}>発電所スペック — {spec.label}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "設備容量", value: `${spec.capacity} MW`, icon: "ri-flashlight-line", color: "text-amber-400" },
              { label: "設計効率", value: `${spec.efficiency}%`, icon: "ri-settings-3-line", color: "text-cyan-400" },
              { label: "CO₂排出量", value: `${spec.co2} t/MWh`, icon: "ri-leaf-line", color: spec.co2 < 0.1 ? "text-emerald-400" : "text-red-400" },
              { label: "月次メンテ費", value: `${plant.maintenanceCost.toLocaleString()} coins`, icon: "ri-tools-line", color: "text-red-400" },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl p-4 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${item.icon} ${item.color} text-sm`}></i>
                  <span className={`text-xs ${textSecondary}`}>{item.label}</span>
                </div>
                <p className={`font-bold ${textPrimary}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <ContractPanel plant={plant} isOwner={isOwner} />
      </div>
      <AuthModal />
    </div>
  );
}
